import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Student, Class, AttendanceRecord, SavedFilter, BehaviorIncident, TeacherAttendance } from '../types';
import { studentService, classService, attendanceService, filterService, behaviorService, teacherAttendanceService } from '../services/apiService';
import classStudentService, { StudentWithEnrollment, ClassWithEnrollment } from '../services/classStudentService';
import { useAuth } from './AuthContext';

interface AppContextType {
  students: Student[];
  classes: Class[];
  attendanceRecords: AttendanceRecord[];
  behaviorIncidents: BehaviorIncident[];
  teacherAttendance: TeacherAttendance[];
  savedFilters: SavedFilter[];
  selectedClass: string | null;
  selectedDate: string;
  loading: boolean;
  error: string | null;
  
  // Actions
  addStudent: (student: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>) => Promise<any>;
  updateStudent: (id: string, updates: Partial<Student>) => Promise<void>;
  deleteStudent: (id: string) => Promise<void>;
  
  addClass: (classData: Omit<Class, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateClass: (id: string, updates: Partial<Class>) => Promise<void>;
  deleteClass: (id: string) => Promise<void>;
  
  // Class-Student relationship management
  getStudentsInClass: (classId: string) => Promise<StudentWithEnrollment[]>;
  getClassesForStudent: (studentId: string) => Promise<ClassWithEnrollment[]>;
  addStudentToClass: (classId: string, studentId: string) => Promise<void>;
  removeStudentFromClass: (classId: string, studentId: string) => Promise<void>;
  getAvailableStudents: (classId: string) => Promise<Student[]>;
  
  markAttendance: (studentId: string, status: string, notes?: string) => Promise<void>;
  updateAttendance: (id: string, updates: Partial<AttendanceRecord>) => Promise<void>;
  bulkMarkAttendance: (studentIds: string[], status: string) => Promise<void>;
  
  addBehaviorIncident: (incident: Omit<BehaviorIncident, 'id'>) => Promise<void>;
  updateBehaviorIncident: (id: string, updates: Partial<BehaviorIncident>) => Promise<void>;
  deleteBehaviorIncident: (id: string) => Promise<void>;
  
  markTeacherAttendance: (classId: string, status: 'present' | 'absent', notes?: string) => Promise<void>;
  updateTeacherAttendance: (id: string, updates: Partial<TeacherAttendance>) => Promise<void>;
  deleteTeacherAttendance: (id: string) => Promise<void>;
  
  
  saveFilter: (filter: Omit<SavedFilter, 'id' | 'createdAt'>) => Promise<void>;
  deleteFilter: (id: string) => Promise<void>;
  
  setSelectedClass: (classId: string | null) => void;
  setSelectedDate: (date: string) => void;
  refreshData: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [behaviorIncidents, setBehaviorIncidents] = useState<BehaviorIncident[]>([]);
  const [teacherAttendance, setTeacherAttendance] = useState<TeacherAttendance[]>([]);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading data from database...');
      const [studentsData, classesData, teacherAttendanceData, filtersData] = await Promise.all([
        studentService.getAll(),
        classService.getAll(),
        teacherAttendanceService.getAll(),
        filterService.getAll()
      ]);
      
      console.log('Data loaded from database:', {
        students: studentsData.length,
        classes: classesData.length,
        teacherAttendance: teacherAttendanceData.length,
        filters: filtersData.length
      });
      
      console.log('Students from database:', studentsData);
      console.log('Sample student data:', studentsData[0]);
      console.log('Classes from database:', classesData);
      
      console.log('Setting state with data...');
      setStudents(studentsData);
      setClasses(classesData);
      setTeacherAttendance(teacherAttendanceData);
      setSavedFilters(filtersData);
      console.log('State set successfully');
      
      // Load class-specific data if a class is selected
      if (selectedClass) {
        await loadClassData();
      } else {
        setAttendanceRecords([]);
        setBehaviorIncidents([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadClassData = async () => {
    if (!selectedClass) return;
    
    try {
      console.log('Loading class-specific data for class:', selectedClass);
      const [attendanceData, behaviorData] = await Promise.all([
        attendanceService.getByDateAndClass(selectedDate, selectedClass),
        behaviorService.getByClass(selectedClass, selectedDate, selectedDate)
      ]);
      setAttendanceRecords(attendanceData);
      setBehaviorIncidents(behaviorData);
      console.log('Class data loaded:', { attendance: attendanceData.length, behavior: behaviorData.length });
    } catch (err) {
      console.error('Error loading class data:', err);
    }
  };

  // Load initial data on app start (only if user is authenticated)
  useEffect(() => {
    if (authLoading) {
      return; // Wait for auth to finish loading
    }
    
    if (!user) {
      setLoading(false);
      return; // Don't load data if user is not authenticated
    }
    
    console.log('User authenticated, loading data...');
    loadData();
  }, [user, authLoading]);

  // Load class-specific data when class or date changes
  useEffect(() => {
    if (!user || authLoading) {
      return; // Don't load if not authenticated
    }
    
    console.log('Class/date changed - loading class data...');
    if (selectedClass) {
      loadClassData();
    }
  }, [selectedClass, selectedDate, user, authLoading]);

  // Student actions
  const addStudent = async (student: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const result = await studentService.create(student);
      console.log('Student created with result:', result);
      // Refresh data from database to ensure consistency
      await loadData();
      return result; // Return the full result object
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add student');
      throw err;
    }
  };

  const updateStudent = async (id: string, updates: Partial<Student>) => {
    try {
      await studentService.update(id, updates);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update student');
      throw err;
    }
  };

  const deleteStudent = async (id: string) => {
    try {
      await studentService.delete(id);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete student');
      throw err;
    }
  };

  // Class actions
  const addClass = async (classData: Omit<Class, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      console.log('Creating class with data:', classData);
      const result = await classService.create(classData);
      console.log('Class created successfully with ID:', result);
      // Refresh data from database to ensure consistency
      await loadData();
      console.log('Data refreshed after class creation');
    } catch (err) {
      console.error('Error creating class:', err);
      setError(err instanceof Error ? err.message : 'Failed to add class');
      throw err;
    }
  };

  const updateClass = async (id: string, updates: Partial<Class>) => {
    try {
      await classService.update(id, updates);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update class');
      throw err;
    }
  };

  const deleteClass = async (id: string) => {
    try {
      await classService.delete(id);
      // Clear selection if deleted class was selected
      if (selectedClass === id) {
        setSelectedClass(null);
      }
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete class');
      throw err;
    }
  };

  // Attendance actions
  const markAttendance = async (studentId: string, status: string, notes?: string) => {
    try {
      if (!selectedClass) throw new Error('No class selected');
      
      // Use the upsert endpoint to handle both create and update
      await attendanceService.create({
        studentId,
        classId: selectedClass,
        date: selectedDate,
        status: status as any,
        notes
      });
      
      // Refresh attendance data from database to ensure consistency
      const attendanceData = await attendanceService.getByDateAndClass(selectedDate, selectedClass);
      setAttendanceRecords(attendanceData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark attendance');
      throw err;
    }
  };

  const updateAttendance = async (id: string, updates: Partial<AttendanceRecord>) => {
    try {
      await attendanceService.update(id, updates);
      // Refresh attendance data from database to ensure consistency
      if (selectedClass) {
        const attendanceData = await attendanceService.getByDateAndClass(selectedDate, selectedClass);
        setAttendanceRecords(attendanceData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update attendance');
      throw err;
    }
  };

  const bulkMarkAttendance = async (studentIds: string[], status: string) => {
    try {
      if (!selectedClass) throw new Error('No class selected');
      
      // Process each student individually using upsert logic
      for (const studentId of studentIds) {
        await attendanceService.create({
          studentId,
          classId: selectedClass,
          date: selectedDate,
          status: status as any,
          notes: ''
        });
      }
      
      // Refresh attendance data from database to ensure consistency
      const attendanceData = await attendanceService.getByDateAndClass(selectedDate, selectedClass);
      setAttendanceRecords(attendanceData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk mark attendance');
      throw err;
    }
  };

  // Behavior incident actions
  const addBehaviorIncident = async (incident: Omit<BehaviorIncident, 'id'>) => {
    try {
      await behaviorService.create(incident);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add behavior incident');
      throw err;
    }
  };

  const updateBehaviorIncident = async (id: string, updates: Partial<BehaviorIncident>) => {
    try {
      await behaviorService.update(id, updates);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update behavior incident');
      throw err;
    }
  };

  const deleteBehaviorIncident = async (id: string) => {
    try {
      await behaviorService.delete(id);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete behavior incident');
      throw err;
    }
  };

  // Teacher attendance actions
  const markTeacherAttendance = async (classId: string, status: 'present' | 'absent', notes?: string) => {
    try {
      // Use the upsert endpoint to handle both create and update
      await teacherAttendanceService.create({
        classId,
        date: selectedDate,
        status,
        notes
      });
      
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark teacher attendance');
      throw err;
    }
  };

  const updateTeacherAttendance = async (id: string, updates: Partial<TeacherAttendance>) => {
    try {
      await teacherAttendanceService.update(id, updates);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update teacher attendance');
      throw err;
    }
  };

  const deleteTeacherAttendance = async (id: string) => {
    try {
      await teacherAttendanceService.delete(id);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete teacher attendance');
      throw err;
    }
  };


  // Filter actions
  const saveFilter = async (filter: Omit<SavedFilter, 'id' | 'createdAt'>) => {
    try {
      await filterService.create(filter);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save filter');
      throw err;
    }
  };

  const deleteFilter = async (id: string) => {
    try {
      await filterService.delete(id);
      // Refresh data from database to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete filter');
      throw err;
    }
  };

  const refreshData = async () => {
    await loadData();
  };

  // Class-Student relationship actions
  const getStudentsInClass = async (classId: string): Promise<StudentWithEnrollment[]> => {
    try {
      return await classStudentService.getStudentsInClass(classId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get students in class');
      throw err;
    }
  };

  const getClassesForStudent = async (studentId: string): Promise<ClassWithEnrollment[]> => {
    try {
      return await classStudentService.getClassesForStudent(studentId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get classes for student');
      throw err;
    }
  };

  const addStudentToClass = async (classId: string, studentId: string): Promise<void> => {
    try {
      await classStudentService.addStudentToClass(classId, studentId);
      // Refresh data to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add student to class');
      throw err;
    }
  };

  const removeStudentFromClass = async (classId: string, studentId: string): Promise<void> => {
    try {
      await classStudentService.removeStudentFromClass(classId, studentId);
      // Refresh data to ensure consistency
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove student from class');
      throw err;
    }
  };

  const getAvailableStudents = async (classId: string): Promise<Student[]> => {
    try {
      return await classStudentService.getAvailableStudents(classId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get available students');
      throw err;
    }
  };

  const value: AppContextType = {
    students,
    classes,
    attendanceRecords,
    behaviorIncidents,
    teacherAttendance,
    savedFilters,
    selectedClass,
    selectedDate,
    loading,
    error,
    
    addStudent,
    updateStudent,
    deleteStudent,
    
    addClass,
    updateClass,
    deleteClass,
    
    // Class-Student relationship management
    getStudentsInClass,
    getClassesForStudent,
    addStudentToClass,
    removeStudentFromClass,
    getAvailableStudents,
    
    markAttendance,
    updateAttendance,
    bulkMarkAttendance,
    
    addBehaviorIncident,
    updateBehaviorIncident,
    deleteBehaviorIncident,
    
    markTeacherAttendance,
    updateTeacherAttendance,
    deleteTeacherAttendance,
    
    
    saveFilter,
    deleteFilter,
    
    setSelectedClass,
    setSelectedDate,
    refreshData
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
